import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { Box, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { selectUnderlyingFutureExpiries } from '../../../selectors/futures'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { FutureSecurity } from '../../../proto/generated/future'
import useSetPrimarySecurity from '../../../hooks/useSetPrimarySecurity'
import AnalyticsManager from '../../../analytics/AnalyticsManager'
import { ExtraSmallButton, NonInteractiveTooltip } from '../../../components/custom'
import { ChartContext } from '../../../components/chartV2/chart.context'
import { fetchFutureExpiries } from '../../../util/futures'
import { setFutureExpiries } from '../../../reducer/futures.reducer'
import { ChartViewKey } from '../../../assets/constants'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../../assets/routes'
import { selectActiveWidget } from '../../../selectors/widgetDashboard'
import { selectActiveLayoutId } from '../../../selectors/home'
import { WidgetTypes } from '../../../components/widgetDashboard/widgetDashboard.enums'
import { updateFutureChart } from '../../../reducer/chart.reducer'
import { selectActiveChartId, selectFutureConfigChartSecurityKey } from '../../../selectors/chart'
import { selectPrimarySecurity } from '../../../selectors/home'
import ThinArrowRightIcon from '../../../assets/icons/ThinArrowRightIcon'
import { selectIsEmbedded } from '../../../selectors/device.ts'
import { useUser } from '../../../context/UserContext.tsx'
import { FeatureCategory, FeatureName } from '../../../components/adminUserRestrictions/Restrictions.constants.ts'
import { Group, setFeatureRestrictionDialog } from '../../../reducer/user.reducer.ts'
import { FeatureCode } from '../../../util/RestrictionFeatureCode.ts'
import { callNative } from '../../../util/nativeBridge'

export type Props = {
    defaultName?: string
    chartSecurityKey?: string
}
const FutureExpiryMenu: React.FC<Props> = ({ defaultName = undefined, chartSecurityKey }: Props) => {
    const {user :{ hasAccess }} = useUser()
    const navigate = useNavigate()
    const setPrimarySecurity = useSetPrimarySecurity()
    const [menuAnchorEl, setMenuAnchorEl] = useState(null)
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState(null)
    const chartContext = useContext(ChartContext)
    const viewKey = useSelector(selectActiveChartId)
    const activeLayoutId = useSelector(selectActiveLayoutId)
    const activeWidget = useSelector(selectActiveWidget(activeLayoutId))
    const activeChartId = chartContext?.viewKey
    const dispatch = useDispatch()
    const chartSecurities = chartContext?.chart?.securities
    const { key: primarySecurityKey } = useSelector(selectPrimarySecurity)
    const futureChartSecurityKey = useSelector(selectFutureConfigChartSecurityKey(activeChartId ?? ''))
    const underlyingToken = chartSecurities?.length ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].underlyingSecurityCode ?? '') : ''
    const contextChartKey = chartSecurities?.length
        ? getSecurityKey(chartSecurities[0].exchange, chartSecurities[0].code)
        : ''
    const key = futureChartSecurityKey || chartSecurityKey || contextChartKey || primarySecurityKey
    const [selectedChartSecurity, setSelectedChartSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [underlying, setUnderlying] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    useEffect(() => { if (key) getSecurityAsync(key).then(setSelectedChartSecurity) }, [key])
    useEffect(() => { if (underlyingToken) getSecurityAsync(underlyingToken).then(setUnderlying) }, [underlyingToken])
    const expiries = useSelector(selectUnderlyingFutureExpiries(underlyingToken))
    const isEmbedded = useSelector(selectIsEmbedded)
    const [selectedExpiry] = useMemo(() => expiries.current.concat(expiries.past).filter((v) => getSecurityKey(v.security!.exchange, v.security!.code) === key), [expiries, key])
    const hasPastExpiryAccess = hasAccess(FeatureCategory.CHART, '', FeatureName.PAST_EXPIRY)

    const handleMenuClick = (event: any) => {
        if (isEmbedded) {
            callNative('onSeriesMetaDataFutureExpiryClicked')
        } else {
            if (!expiries.current.length && (selectedChartSecurity || underlying)) {
                const securityToFetch = selectedChartSecurity?.hasFo ? selectedChartSecurity : underlying
                fetchFutureExpiries(securityToFetch!).then(([currentExpiries, pastExpiries]) => {
                    dispatch(
                        setFutureExpiries({
                            token: getSecurityKey(securityToFetch!.exchange, securityToFetch!.code),
                            data: {
                                current: currentExpiries,
                                past: pastExpiries
                            }
                        })
                    )
                })
            }
            setMenuAnchorEl(event.currentTarget)
        }
    }

    const handleMenuClose = () => {
        setMenuAnchorEl(null)
        setSubmenuAnchorEl(null)
    }

    const openSubmenu = (event: any) => {
        if(!hasAccess(FeatureCategory.CHART, '', FeatureName.PAST_EXPIRY)){
            dispatch(setFeatureRestrictionDialog({
                open: true,
                feature: "Past expiries",
                featureRestrictionCode:FeatureCode.PAST_EXPIRY,
                group: Group.CHART
            }))
            return
        }
        setSubmenuAnchorEl(event.currentTarget)
    }

    const handleFutureChange = (future: FutureSecurity) => {
        if (future.security) {
            setSelectedChartSecurity(future.security)
            if (activeChartId === ChartViewKey.SYMBOL) {
                navigate(generatePath(ROUTES.symbol, { symbol: getSecurityKey(future.security.exchange, future.security.code) }))
            } else if (activeWidget?.widgetType === WidgetTypes.FUTURE_CHART) {
                dispatch(updateFutureChart({ viewKey, config: { key, securityKey: getSecurityKey(future.security.exchange, future.security.code) }, createIfAbsent: true }))
            } else {
                setPrimarySecurity(getSecurityKey(future.security!.exchange, future.security!.code), [future.security!], false)
            }

            AnalyticsManager.logEvent('Future Charts Expiry Changed', { selected_value: future.security.code })
        }
        handleMenuClose()
    }

    const expiryLabel = useMemo(() => {
        if (!selectedChartSecurity?.expiry?.name) return ''
        return selectedChartSecurity?.expiry?.name
    }, [selectedChartSecurity])

    return (
        <>
            <NonInteractiveTooltip placement={'top'} title="Change Expiry" arrow>
                <ExtraSmallButton
                    size="small"
                    variant="text"
                    onClick={handleMenuClick}
                    color="inherit"
                    sx={{ padding: 0, margin: 0, display: 'inline-block', maxHeight: '24px', minWidth: 0, fontSize: '0.875rem' }}
                >
                    {defaultName && !selectedChartSecurity?.derived ? `${expiryLabel}(${defaultName})` : expiryLabel}
                </ExtraSmallButton>
            </NonInteractiveTooltip>
            {Boolean(menuAnchorEl) && !isEmbedded && (
                <Menu
                    MenuListProps={{
                        onMouseLeave: () => {
                            handleMenuClose()
                        }
                    }}
                    id="basic-menu"
                    anchorEl={menuAnchorEl}
                    open={Boolean(menuAnchorEl)}
                    autoFocus={true}
                    onClose={handleMenuClose}
                >
                    {expiries.current?.map((future) => (
                        <MenuItem
                            key={future.security?.code}
                            selected={future.expiry?.name === selectedExpiry?.expiry?.name}
                            onClick={() => {
                                handleFutureChange(future)
                            }}
                        >
                            {future.expiry?.name}
                        </MenuItem>
                    ))}
                        <MenuItem
                            onClick={openSubmenu}
                            selected={expiries.past?.some(
                                (ex) => ex.expiry.name === expiryLabel
                            )}
                        >
                            <Box
                                sx={{
                                display: 'flex',
                                alignItems: 'center',
                                width: '100%'
                                }}
                            >
                                <ListItemText>Past Expiry</ListItemText>
                                <ListItemIcon  sx={{ minWidth: 24 }}>
                                    <ThinArrowRightIcon fontSize="small" />
                                </ListItemIcon>
                            </Box>
                        </MenuItem>
                    <Menu
                        MenuListProps={{
                            onMouseLeave: () => {
                                setSubmenuAnchorEl(null)
                            }
                        }}
                        id="submenu"
                        anchorEl={submenuAnchorEl}
                        open={Boolean(submenuAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                    >
                        {expiries.past?.map((future) => (
                            <MenuItem
                                key={future.security?.code}
                                selected={future.expiry?.name === selectedExpiry?.expiry?.name}
                                onClick={() => {
                                    handleFutureChange(future)
                                }}
                            >
                                {future.expiry?.name}
                            </MenuItem>
                        ))}
                    </Menu>
                </Menu>
            )}
        </>
    )
}

export default memo(FutureExpiryMenu)
